import React from 'react';
import { Loader2 } from 'lucide-react';

export const Input = ({ label, name, register, errors, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
    <input
      {...register?.(name)}
      {...props}
      className={`w-full px-4 py-2.5 rounded-lg border ${errors?.[name] ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-slate-300 focus:border-purple-500 focus:ring-purple-500/20'} focus:ring-2 outline-none transition-all text-sm`}
    />
    {errors?.[name] && <p className="mt-1 text-xs text-red-500">{errors[name].message}</p>}
  </div>
);

export const TextArea = ({ label, name, register, errors, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
    <textarea
      {...register?.(name)}
      {...props}
      className={`w-full px-4 py-2.5 rounded-lg border ${errors?.[name] ? 'border-red-300' : 'border-slate-300 focus:border-purple-500 focus:ring-purple-500/20'} focus:ring-2 outline-none transition-all text-sm resize-none`}
    />
    {errors?.[name] && <p className="mt-1 text-xs text-red-500">{errors[name].message}</p>}
  </div>
);

export const Select = ({ label, name, register, errors, options, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>}
    <select
      {...register?.(name)}
      {...props}
      className={`w-full px-4 py-2.5 rounded-lg border ${errors?.[name] ? 'border-red-300' : 'border-slate-300 focus:border-purple-500 focus:ring-purple-500/20'} focus:ring-2 outline-none transition-all text-sm bg-white`}
    >
      <option value="">Select...</option>
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    {errors?.[name] && <p className="mt-1 text-xs text-red-500">{errors[name].message}</p>}
  </div>
);

export const Button = ({ children, loading, variant = 'primary', ...props }) => {
  const variants = {
    primary: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg shadow-purple-500/25',
    secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200',
    danger: 'bg-red-500 text-white hover:bg-red-600',
    success: 'bg-emerald-500 text-white hover:bg-emerald-600',
  };

  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant] || variants.primary}`}
    >
      {loading && <Loader2 size={16} className="animate-spin" />}
      {children}
    </button>
  );
};

export const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${sizes[size]} max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};
